import { Link } from 'react-router-dom'
import type { Project } from '../../types'

/**
 * The stage this invention has reached, in the words the rest of the product
 * uses. Read in the order the lifecycle actually runs, so the label names the
 * furthest point reached and never a step already passed:
 *
 *   Archived / Draft   the project's own status, when it is not `active`
 *   Analysis           an AI analysis is running or has failed
 *   Review             analysed, and the owner has not approved matching yet
 *   Matching           analysed and approved — the invention is being matched
 *   Intake             nothing uploaded yet
 *
 * Every input is a field the payload already carries; nothing here is derived
 * from a count or invented for the sake of having a word to print.
 */
export function stageLabel(project: Project): string {
  if (project.status === 'archived') return 'Archived'
  if (project.status === 'draft') return 'Draft'
  if (project.ai_analysis_status === 'failed') return 'Analysis failed'
  if (project.ai_analysis_status === 'pending' || project.ai_analysis_status === 'in_progress') {
    return 'Analysis'
  }
  if (project.document_count === 0) return 'Intake'
  if (project.review_status === 'approved') return 'Matching'
  return 'Review'
}

export interface YoursNotPartOfPortfolioProps {
  projects: Project[]
  /** The portfolio's own name, for the "grant it to <name>" sentence. */
  portfolioName?: string
  /** S5's variant of the closing line — never drawn on the entity map either. */
  variant?: 'portfolio' | 'entity'
}

/**
 * Figures S6 and S5: the viewer's own inventions, LISTED and never plotted —
 * the one place the two universes meet, and it must stay a list.
 *
 * These are NOT portfolio data: never counted in a group, matrix, topology or
 * brief, and never drawn on the atlas or the entity map. This component must
 * never feed a chart, a map, or an aggregate — a plain list only. The dashed
 * border is the figure's way of saying the card is outside the portfolio.
 */
export default function YoursNotPartOfPortfolio({
  projects,
  portfolioName,
  variant = 'portfolio',
}: YoursNotPartOfPortfolioProps) {
  return (
    <div
      data-part="YoursNotPartOfPortfolio"
      className="rounded-xl border border-dashed border-iflow-900/20 bg-white/60 p-4"
    >
      <h3 className="text-[15px] font-bold text-iflow-900">Yours — not part of Portfolio</h3>
      <p className="mt-0.5 text-[12px] text-gray-500">
        {projects.length} invention{projects.length === 1 ? '' : 's'} · listed, never plotted
      </p>

      {projects.length === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          You own no inventions outside this portfolio.
        </p>
      ) : (
        <ul className="mt-2">
          {projects.map((project) => (
            <li
              key={project.project_id}
              data-testid="yours-not-in-portfolio-row"
              className="flex items-start justify-between gap-3 border-t border-iflow-900/5 py-2 first:border-t-0"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-iflow-900">{project.name}</div>
                <div className="text-[11.5px] text-gray-500">
                  Yours · {stageLabel(project)} · {project.document_count} document
                  {project.document_count === 1 ? '' : 's'}
                </div>
              </div>
              <Link
                to={`/project/${project.project_id}`}
                title="Zoom in to Invention"
                className="shrink-0 text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
              >
                Open ▸
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">
        Drawn for orientation; <b className="text-iflow-800">not counted in any group, matrix,
        topology or brief</b>
        {variant === 'entity'
          ? ' — and never drawn on the entity map.'
          : ' — listed here, never plotted on the atlas.'}
        {variant === 'portfolio' && (
          <>
            {' '}To put one in the portfolio, grant it to {portfolioName || 'this portfolio'} like any
            other inventor.
          </>
        )}
      </p>
    </div>
  )
}
